import * as THREE from 'three'

import { clamp } from '../util/units.js'

/**
 * Improved Fresnel Lens Optical Landing System — "the ball".
 *
 * The whole recovery is flown off this object, so it gets two unusual
 * treatments:
 *
 *  1. The ball and the datum lights are sprites with a minimum apparent size.
 *     A physically-sized light source 1.4 km away is a fraction of a pixel and
 *     would simply not exist on screen. The real lens is visible at that range
 *     because it is blindingly bright; clamping the sprite's angular size is
 *     the honest way to reproduce that in a renderer without HDR bloom.
 *
 *  2. The ball moves continuously rather than snapping between the twelve
 *     physical cells. For a trainer, a ball that visibly creeps is far more
 *     instructive than one that jumps a cell at a time.
 */

function glowTexture(color, softness = 0.55) {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  g.addColorStop(0, color)
  g.addColorStop(softness, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/** Sprite that never shrinks below a minimum angular size. */
class ClampedSprite {
  constructor(texture, baseSizeM, minAngular) {
    this.material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
    })
    this.sprite = new THREE.Sprite(this.material)
    this.baseSizeM = baseSizeM
    this.minAngular = minAngular
    this._worldPos = new THREE.Vector3()
  }

  update(cameraPosition) {
    this.sprite.getWorldPosition(this._worldPos)
    const dist = this._worldPos.distanceTo(cameraPosition)
    const size = Math.max(this.baseSizeM, dist * this.minAngular)
    this.sprite.scale.set(size, size, 1)
  }
}

export class IFLOLS {
  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'IFLOLS'

    this.ballNorm = 0
    this.ballVisible = true
    this.waveoff = false
    this.cut = false
    this._blink = 0

    /** Vertical travel of the ball for +/- full scale, metres. */
    this.railHalfHeight = 1.25

    this._buildHousing()
    this._buildLights()

    this._sprites = [this.ball, ...this.datums, ...this.waveoffLights, this.cutLight]
  }

  _buildHousing() {
    const housingMat = new THREE.MeshStandardMaterial({
      color: 0x1c2024,
      roughness: 0.85,
      metalness: 0.25,
    })

    // Lens box.
    const box = new THREE.Mesh(new THREE.BoxGeometry(1.1, 3.0, 0.9), housingMat)
    box.position.y = 1.9
    this.group.add(box)

    // Stabilised platform legs.
    for (const x of [-1.7, 1.7]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.18, 1.0, 0.18), housingMat)
      leg.position.set(x, 0.5, 0)
      this.group.add(leg)
    }

    // Datum light bars, one each side.
    for (const x of [-2.2, 2.2]) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.28, 0.3), housingMat)
      bar.position.set(x, 1.9, 0)
      this.group.add(bar)
    }
  }

  _buildLights() {
    const amber = glowTexture('rgba(255,178,46,1)')
    const green = glowTexture('rgba(90,255,150,1)')
    const red = glowTexture('rgba(255,64,48,1)')

    // The ball itself.
    this.ball = new ClampedSprite(amber, 0.9, 0.0042)
    this.ball.sprite.position.set(0, 1.9, 0.5)
    this.group.add(this.ball.sprite)

    // Green datum lights: the reference the ball is read against.
    this.datums = []
    for (const x of [-2.9, -2.1, 2.1, 2.9]) {
      const d = new ClampedSprite(green, 0.55, 0.0026)
      d.sprite.position.set(x, 1.9, 0.35)
      this.group.add(d.sprite)
      this.datums.push(d)
    }

    // Wave-off lights, outboard and above.
    this.waveoffLights = []
    for (const x of [-3.6, 3.6]) {
      const w = new ClampedSprite(red, 0.7, 0.0032)
      w.sprite.position.set(x, 2.9, 0.35)
      w.sprite.visible = false
      this.group.add(w.sprite)
      this.waveoffLights.push(w)
    }

    // Cut light.
    this.cutLight = new ClampedSprite(green, 0.7, 0.0032)
    this.cutLight.sprite.position.set(0, 3.5, 0.35)
    this.cutLight.sprite.visible = false
    this.group.add(this.cutLight.sprite)
  }

  /**
   * @param {number} ballNorm -1 low, 0 centred, +1 high. Beyond +/-1.25 the
   *        ball is off the lens and stops being drawn, exactly as it would be.
   * @param {boolean} inRange the lens is only meaningful once you are in the
   *        groove; outside that it is switched off rather than lying to you.
   */
  setBall(ballNorm, inRange = true) {
    this.ballNorm = ballNorm
    this.ballVisible = inRange && Math.abs(ballNorm) < 1.25
  }

  setWaveoff(on) {
    this.waveoff = on
  }

  setCut(on) {
    this.cut = on
  }

  /**
   * @param {THREE.Vector3} cameraPosition
   * @param {number} dt
   */
  update(cameraPosition, dt) {
    this._blink += dt

    this.ball.sprite.position.y = 1.9 + clamp(this.ballNorm, -1.15, 1.15) * this.railHalfHeight
    this.ball.sprite.visible = this.ballVisible && !this.waveoff

    // A pegged ball flashes, which is the lens telling you it has run out of
    // information before the LSO does.
    if (this.ballVisible && Math.abs(this.ballNorm) > 0.95) {
      this.ball.material.opacity = this._blink % 0.5 < 0.25 ? 1 : 0.25
    } else {
      this.ball.material.opacity = 1
    }

    const flash = this._blink % 0.4 < 0.2
    for (const w of this.waveoffLights) {
      w.sprite.visible = this.waveoff && flash
    }
    this.cutLight.sprite.visible = this.cut && flash

    for (const s of this._sprites) {
      if (s.sprite.visible) s.update(cameraPosition)
    }
  }

  dispose() {
    this.group.traverse((o) => {
      if (o.geometry) o.geometry.dispose()
      if (o.material) {
        o.material.map?.dispose?.()
        o.material.dispose()
      }
    })
  }
}
